import { callSNOWDataService } from 'src/utils/snowDataServiceWrapper';
import { getAwsCredentials } from 'src/authentication/cognito';
import { logger } from 'src/utils/logger';
import { v4 as uuidv4 } from 'uuid';
import { OLAF_WORKFLOW_BINDLE_DATA } from 'src/authentication/bindle-utils';
import AWS from 'aws-sdk';
import { SessionStorageCache } from './sessionStorageCache';

interface SDSCredentials {
	accessKey: string;
	secretKey: string;
	sessionToken?: string;
}

// Permission cache 

const PERMISSION_CACHE_KEY_PREFIX = 'snow_permission_cache';
const PERMISSION_CACHE_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours

interface PermissionCacheData {
	authorizedIds: string[];
	workflowIds: string[];
}

function getPermissionCacheKey(userName: string, action: string): string {
	return `${PERMISSION_CACHE_KEY_PREFIX}_${userName}_${action}`;
}

function permissionCache(
	userName: string,
	action: string
): SessionStorageCache<PermissionCacheData> {
	return new SessionStorageCache<PermissionCacheData>(
		getPermissionCacheKey(userName, action),
		PERMISSION_CACHE_TTL_MS
	);
}

function getCachedPermissions(userName: string, action: string, workflowIds: string[]): string[] | null {
	const cache = permissionCache(userName, action);
	const cached = cache.get();
	if (!cached) {
		return null;
	}

	// If the workflow list changed (new workflows added/removed), invalidate
	const cachedIds = new Set(cached.workflowIds);
	const currentIds = new Set(workflowIds);
	if (cachedIds.size !== currentIds.size || workflowIds.some(id => !cachedIds.has(id))) {
		cache.invalidate();
		logger.info(`Permission cache MISS (${action}): workflow list changed`);
		return null;
	}

	return cached.authorizedIds;
}

function setCachedPermissions(userName: string, action: string, workflowIds: string[], authorizedIds: string[]): void {
	permissionCache(userName, action).set({ authorizedIds, workflowIds });
}

export function invalidatePermissionCache(): void {
	SessionStorageCache.invalidateByPrefix(PERMISSION_CACHE_KEY_PREFIX);
}

//Permission check with caching

// Batches requests in chunks of 100 due to API limit
export async function checkWorkflowPermissions(
	workflowIds: string[],
	userName: string,
	action: 'edit' | 'execute' | 'submit'
): Promise<string[]> {
	const cached = getCachedPermissions(userName, action, workflowIds);
	if (cached !== null) {
		return cached;
	}

	try {
		await getAwsCredentials();
		
		if (!AWS.config.credentials) {
			logger.error('AWS credentials not available');

			return [];
		}

		const credentials: SDSCredentials = {
			accessKey: AWS.config.credentials.accessKeyId,
			secretKey: AWS.config.credentials.secretAccessKey,
			sessionToken: AWS.config.credentials.sessionToken,
		};

		const BATCH_SIZE = 100;
		
		const batches: string[][] = [];

		for (let i = 0; i < workflowIds.length; i += BATCH_SIZE) {
			batches.push(workflowIds.slice(i, i + BATCH_SIZE));
		}

		const batchPromises = batches.map((batch) => {
			const resource = {
				type: OLAF_WORKFLOW_BINDLE_DATA.type,
				namespace: OLAF_WORKFLOW_BINDLE_DATA.namespace,
				action,
				ids: batch,
			};

			return callSNOWDataService({
				path: '/authorize',
				method: 'POST',
				body: {
					user: userName,
					resource,
					requestId: uuidv4(),
				},
				credentials: credentials as unknown as null,
			});
		});

		const responses = await Promise.all(batchPromises);
		
		const allAuthorizedIds: string[] = [];

		for (const apiResponse of responses) {
			const authorizedIds = apiResponse.authorizedResource.ids || [];

			allAuthorizedIds.push(...authorizedIds);
		}

		setCachedPermissions(userName, action, workflowIds, allAuthorizedIds);

		logger.info(
			`${action} permission check for ${workflowIds.length} workflows - ${userName}: ${allAuthorizedIds.length} authorized`
		);

		return allAuthorizedIds;
	} catch (e) {
		logger.error(
			`Error checking ${action} permissions for workflows - ${userName}: ${e instanceof Error ? e.message : String(e)}`
		);

		return [];
	}
}
